import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Film } from 'lucide-react';
import { TaskyBadge, TaskyBadgeProps, TaskyProgress } from '../../components/tasky';
import type { RecipeVideoStage, RecipeVideoStageState, RecipeVideoSummary } from '../../services/api/recipeVideoService';
import { useRecipeVideoStrings } from './strings';

/**
 * 一覧の「作成中」区画のカード（作成中・準備中の動画レシピ1件）。
 *
 * 今の段の名前と n/m（仕上げは compose の中の段階）、段の一覧（済み/今/これから）、全体の%、経過時間を出す。
 * 経過時間は今の作成を始めた時刻（started_at。反映・やり直しで変わる）から数える。これからの段は数を出さない
 * （反映で作り直すとき、前回の出来上がりはその段に入るまで数え直されない。サーバーは done=null で返す）。
 * 段の中身はサーバーの stages をそのまま描く（当てはまらない段はサーバーが出さない）。
 * カード全体がタイトルのリンクで詳細（編集ページ）へ行く。
 */

const STAGE_VARIANT: Record<RecipeVideoStageState, TaskyBadgeProps['variant']> = {
  done: 'success',
  current: 'info',
  pending: 'secondary',
  failed: 'destructive',
};

const count = (stage: RecipeVideoStage): string | null =>
  stage.total !== null && stage.done !== null ? `${stage.done}/${stage.total}` : null;

/** since からの経過秒。数秒ごとに再描画すれば足りる */
const useElapsedSeconds = (since: string | null, tickMs = 1000): number | null => {
  const start = since ? Date.parse(since) : NaN;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (Number.isNaN(start)) return undefined;
    const id = setInterval(() => setNow(Date.now()), tickMs);
    return () => clearInterval(id);
  }, [start, tickMs]);
  return Number.isNaN(start) ? null : Math.max(0, Math.floor((now - start) / 1000));
};

interface Props {
  project: RecipeVideoSummary;
}

const RecipeVideoProgressCard: React.FC<Props> = ({ project }) => {
  const t = useRecipeVideoStrings();
  const elapsed = useElapsedSeconds(project.started_at ?? project.created_at);
  const current = project.stages.find((s) => s.state === 'current' || s.state === 'failed') ?? null;
  const currentName = current
    ? current.key === 'compose' && current.phase
      ? t.composePhase[current.phase]
      : t.step[current.key]
    : t.step[project.step];
  const currentCount = current ? count(current) : null;

  return (
    <article
      className="relative min-w-0 rounded-tasky-md border border-warm-border bg-white p-4 transition-shadow hover:shadow-card"
      data-testid={`recipe-video-progress-${project.id}`}
    >
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex h-16 w-9 shrink-0 items-center justify-center overflow-hidden rounded bg-gray-100">
          {project.thumbnail_url ? (
            <img src={project.thumbnail_url} alt="" className="h-full w-full object-cover" />
          ) : (
            <Film className="h-4 w-4 text-gray-300" aria-hidden="true" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="min-w-0 truncate text-sm font-semibold text-dark">
              {/* カード全体を押せるようにリンクを広げる（div の onClick にしない） */}
              <Link
                to={`/ads/recipe-video/${project.id}`}
                className="rounded-sm after:absolute after:inset-0 after:content-[''] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {project.title}
              </Link>
            </h3>
            <TaskyBadge variant={project.status === 'pending' ? 'secondary' : 'info'} className="shrink-0">
              {t.status[project.status]}
            </TaskyBadge>
          </div>
          <p className="mt-1 text-sm text-dark" role="status" aria-live="polite" aria-atomic="true">
            {currentName}
            {currentCount && <span className="ml-1.5 tabular-nums text-gray-500">{currentCount}</span>}
          </p>
        </div>
      </div>

      {project.stages.length > 0 && (
        <ol className="mt-3 flex flex-wrap items-center gap-1.5" aria-label={t.stagesLabel}>
          {project.stages.map((stage) => {
            const n = count(stage);
            return (
              <li key={stage.key} aria-current={stage.state === 'current' ? 'step' : undefined}>
                <TaskyBadge variant={STAGE_VARIANT[stage.state]}>
                  {stage.state === 'done' && <Check className="h-3 w-3" aria-hidden="true" />}
                  {t.stage[stage.key]}
                  {n && <span className="tabular-nums">{n}</span>}
                  <span className="sr-only">（{t.stageState[stage.state]}）</span>
                </TaskyBadge>
              </li>
            );
          })}
        </ol>
      )}

      <TaskyProgress
        className="mt-3"
        value={project.progress}
        barLabel={t.progressLabel(project.title)}
        label={elapsed !== null ? <span className="text-xs font-normal text-gray-500">{t.elapsed(elapsed)}</span> : undefined}
        showValue
      />
      <p className="mt-2 text-xs leading-relaxed text-gray-500">{t.progressNote}</p>
    </article>
  );
};

export default RecipeVideoProgressCard;
